import type { AgoraCredentials } from '@advaitnetra/shared';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';

import { ApiFailure, joinSession, linkTokenFromPath, resolveLink } from './api';
import { LICENCES_HASH, isLicencesPath } from './licencesRoute';
import {
  notLiveStatusFromJoinFailure,
  notLiveStatusFromResolve,
  type NotLiveStatus,
} from './linkPhase';
import { initPatientSentry } from './monitoring/sentry';
import { NotLive } from './NotLive';
import { PermissionGate } from './PermissionGate';

/**
 * The Agora SDK loads only when the patient actually joins — see CallSurface
 * for why. Prefetched the moment we know the session is joinable, so the
 * download overlaps the permission prompt instead of following it.
 */
const CallSurface = lazy(() => import('./CallSurface'));
const prefetchCallSurface = () => void import('./CallSurface');

/**
 * The third-party licences (LICENSE-CLEARANCE rows 4/9/10), lazy for the same
 * reason: the notices JSON is ~30 kB the join path does not need.
 */
const LicencesView = lazy(() => import('./LicencesView'));

const setHash = (hash: string) =>
  window.history.replaceState(null, '', window.location.pathname + window.location.search + hash);

type Phase =
  | { kind: 'loading' }
  | { kind: 'not-live'; status: NotLiveStatus }
  | { kind: 'permission' }
  | { kind: 'joining' }
  // linkToken travels with the call phase (not re-read from module scope)
  // so CallRoom can carry it type-safely — SC1/SC3 need it to report a
  // witness and re-resolve the recovery deadline while already in-call.
  | { kind: 'in-call'; credentials: AgoraCredentials; linkToken: string }
  | { kind: 'left' }
  | { kind: 'error'; message: string };

export function App() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const linkToken = linkTokenFromPath(window.location.pathname);
  // The licences view over the join page (opened from its footer line, kept
  // in the hash so a reload reopens it), or standalone at /licences.
  const standaloneLicences = isLicencesPath(window.location.pathname);
  const [showLicences, setShowLicences] = useState(() => window.location.hash === LICENCES_HASH);

  // Sentry can only be initialised once the link token is known — its tunnel
  // route is /api/link/:linkToken/monitoring/tunnel, unlike physio-app whose
  // config is static at launch (see monitoring/sentry.ts's header). Runs once
  // per mount; linkToken itself never changes across this page's lifetime.
  useEffect(() => {
    if (linkToken) initPatientSentry(linkToken);
  }, [linkToken]);

  // Resolve first, WITHOUT minting credentials. This is why the API splits
  // resolve from join: an expired or not-yet-started session should say so
  // before an anxious patient is asked for camera access.
  useEffect(() => {
    if (!linkToken) {
      setPhase({
        kind: 'error',
        message: 'This link is not valid. Please check with your physiotherapist for a new one.',
      });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const resolved = await resolveLink(linkToken);
        if (cancelled) return;
        if (resolved.joinable) {
          // Start pulling the SDK now, while the patient reads the permission
          // prompt and taps Allow. That is several seconds of otherwise idle
          // network time.
          prefetchCallSurface();
          setPhase({ kind: 'permission' });
        } else {
          // D9: the server's reason decides — expired says expired even on a
          // pending session; ended stays ended; only genuinely-pending waits.
          setPhase({ kind: 'not-live', status: notLiveStatusFromResolve(resolved) });
        }
      } catch (err) {
        if (cancelled) return;
        setPhase({
          kind: 'error',
          message: err instanceof ApiFailure ? err.message : 'Something went wrong.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkToken]);

  const join = useCallback(async () => {
    if (!linkToken) return;
    setPhase({ kind: 'joining' });
    try {
      setPhase({ kind: 'in-call', credentials: await joinSession(linkToken), linkToken });
    } catch (err) {
      // Hygiene ③ + D9: a join refusal that names a link state renders as
      // that state, not as a scary generic error — a join racing the physio's
      // start stays the waiting room's truth, expiry gets P4's exact copy,
      // ended stays ended. Anything else remains the generic error screen.
      if (err instanceof ApiFailure) {
        const status = notLiveStatusFromJoinFailure(err.code);
        if (status) {
          setPhase({ kind: 'not-live', status });
          return;
        }
      }
      setPhase({
        kind: 'error',
        message: err instanceof ApiFailure ? err.message : 'We could not connect you.',
      });
    }
  }, [linkToken]);

  const spinner = (
    <div className="screen">
      <div className="spinner" />
    </div>
  );

  if (standaloneLicences) {
    return (
      <Suspense fallback={spinner}>
        <LicencesView />
      </Suspense>
    );
  }
  if (showLicences) {
    return (
      <Suspense fallback={spinner}>
        <LicencesView
          onClose={() => {
            setShowLicences(false);
            setHash('');
          }}
        />
      </Suspense>
    );
  }

  switch (phase.kind) {
    case 'loading':
      return spinner;

    case 'not-live':
      return <NotLive status={phase.status} />;

    case 'permission':
      return (
        <PermissionGate
          onGranted={() => void join()}
          onShowLicences={() => {
            setShowLicences(true);
            setHash(LICENCES_HASH);
          }}
        />
      );

    case 'joining':
      return (
        <div className="screen">
          <div className="spinner" />
          <p>Connecting you now…</p>
        </div>
      );

    case 'in-call':
      return (
        <Suspense
          fallback={
            <div className="screen">
              <div className="spinner" />
              <p>Connecting you now…</p>
            </div>
          }
        >
          <CallSurface
            credentials={phase.credentials}
            linkToken={phase.linkToken}
            onLeave={() => setPhase({ kind: 'left' })}
          />
        </Suspense>
      );

    // Ends on its own page, never on a marketing home (ADR-0007 §7).
    case 'left':
      return (
        <div className="screen">
          <h1>You have left the session</h1>
          <p>You can close this page. Your physiotherapist will be in touch about next steps.</p>
        </div>
      );

    case 'error':
      return (
        <div className="screen">
          <h1>We could not connect you</h1>
          <p>{phase.message}</p>
        </div>
      );
  }
}
